import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/self-hosted-auth";
import { assertAdmin } from "@/lib/access.server";

async function callerEmail(context: { supabase: any; userId: string; claims?: Record<string, unknown> }) {
  const fromClaims = typeof context.claims?.["email"] === "string" ? (context.claims["email"] as string) : null;
  if (fromClaims) return fromClaims;
  const { data } = await context.supabase.from("profiles").select("email").eq("id", context.userId).maybeSingle();
  return (data?.email as string | null) ?? null;
}

type Rules = { domains: string[]; emails: string[] };

/**
 * Baseline allowlist used when no rules can be read. The allowlist tables are
 * admin-only under RLS, and the service-role key may not be configured, so
 * without this fallback every sign-in would be denied.
 */
const DEFAULT_ALLOWED_DOMAINS = (process.env["ALLOWED_EMAIL_DOMAINS"] ?? "lookingforwardconsulting.com")
  .split(",")
  .map((d) => d.trim().toLowerCase())
  .filter(Boolean);

/** Reads the allowlist as the calling user (RLS applies). */
async function loadRules(userClient: {
  from: (table: string) => { select: (cols: string) => Promise<{ data: unknown[] | null; error: unknown }> };
}): Promise<Rules> {
  try {
    const [domains, emails] = await Promise.all([
      userClient.from("allowed_domains").select("domain"),
      userClient.from("allowed_emails").select("email"),
    ]);
    return {
      domains: (domains.data ?? []).map((d) => (d as { domain?: string | null }).domain ?? ""),
      emails: (emails.data ?? []).map((e) => (e as { email?: string | null }).email ?? ""),
    };
  } catch (error) {
    console.error("[access] allowlist read failed, falling back to the baseline domain:", error);
    return { domains: [], emails: [] };
  }
}

/**
 * Server-side exact lookup of the caller's own email/domain. The allowlist
 * tables are admin-only under RLS, so a non-admin external user (email
 * exception) can never see their own row through the user client.
 */
async function serverAllowlistHit(email: string, domain: string): Promise<boolean | null> {
  try {
    const { selfHostedAdmin } = await import("@/integrations/supabase/self-hosted-admin.server");
    const [e, d] = await Promise.all([
      selfHostedAdmin.from("allowed_emails").select("email").eq("email", email).limit(1),
      selfHostedAdmin.from("allowed_domains").select("domain").eq("domain", domain).limit(1),
    ]);
    if (e.error || d.error) {
      console.error("[access] server allowlist lookup failed:", e.error ?? d.error);
      return null;
    }
    return (e.data?.length ?? 0) > 0 || (d.data?.length ?? 0) > 0;
  } catch (error) {
    console.error("[access] server allowlist lookup unavailable:", error);
    return null;
  }
}

async function emailAllowed(email: string | null, userClient: never): Promise<boolean> {
  if (!email) return false;
  const normalized = email.trim().toLowerCase();
  const domain = normalized.split("@")[1] ?? "";
  if (DEFAULT_ALLOWED_DOMAINS.includes(domain)) return true;
  if ((await serverAllowlistHit(normalized, domain)) === true) return true;
  const rules = await loadRules(userClient);
  const domainHit = rules.domains.some((d) => d.trim().toLowerCase() === domain);
  const emailHit = rules.emails.some((e) => e.trim().toLowerCase() === normalized);
  return domainHit || emailHit;
}

/** True only when we can positively confirm the account is deactivated (banned). */
async function isDeactivated(userId: string): Promise<boolean> {
  try {
    const { selfHostedAdmin } = await import("@/integrations/supabase/self-hosted-admin.server");
    const { data, error } = await selfHostedAdmin.auth.admin.getUserById(userId);
    if (error || !data?.user) return false;
    const bannedUntil = (data.user as unknown as Record<string, unknown>)["banned_until"];
    if (!bannedUntil || typeof bannedUntil !== "string" || bannedUntil.toLowerCase() === "none") return false;
    const until = Date.parse(bannedUntil);
    return Number.isNaN(until) ? false : until > Date.now();
  } catch (error) {
    console.error("[access] could not check account status:", error);
    return false;
  }
}

/** Tells the signed-in caller whether their email is permitted to use the tool. */
export const getAccessStatus = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<{ allowed: boolean; email: string | null }> => {
    const email = await callerEmail(context as never);
    if (await isDeactivated(context.userId)) return { allowed: false, email };
    return { allowed: await emailAllowed(email, context.supabase as never), email };
  });

/** Admin view of the approved domains and one-off email exceptions. */
export const listAccessRules = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.supabase, context.userId);
    const [domains, emails] = await Promise.all([
      context.supabase.from("allowed_domains").select("domain, note").order("domain"),
      context.supabase.from("allowed_emails").select("email, note").order("email"),
    ]);
    if (domains.error) throw new Error(domains.error.message);
    if (emails.error) throw new Error(emails.error.message);
    return { domains: domains.data ?? [], emails: emails.data ?? [] };
  });

export const addAccessRule = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z
      .object({
        kind: z.enum(["domain", "email"]),
        value: z.string().min(3).max(200),
        note: z.string().max(300).nullable().optional(),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const value = data.value.trim().toLowerCase().replace(/^@/, "");
    if (data.kind === "email" && !value.includes("@")) throw new Error("Enter a full email address.");
    if (data.kind === "domain" && value.includes("@")) throw new Error("Enter a domain only, without the @.");
    const note = data.note ?? null;
    const { error } =
      data.kind === "domain"
        ? await context.supabase.from("allowed_domains").upsert({ domain: value, note })
        : await context.supabase.from("allowed_emails").upsert({ email: value, note });
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const removeAccessRule = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => z.object({ kind: z.enum(["domain", "email"]), value: z.string().min(1) }).parse(input))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const value = data.value.trim().toLowerCase();
    const { error } =
      data.kind === "domain"
        ? await context.supabase.from("allowed_domains").delete().eq("domain", value)
        : await context.supabase.from("allowed_emails").delete().eq("email", value);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

